/**
 * License Economics – create / edit form.
 *
 * • On create  → builds LicenseEconomicsInput (includes scope fields).
 * • On edit    → builds LicenseEconomicsUpdate (scope-free per new contract).
 *
 * Pre-validates activeSeats <= seats before calling onSubmit.
 * Rendered inside the shared .fi-drawer-body slide-over.
 */

import { useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import type {
  ContractualTrueUpTiming,
  LicenseEconomics,
  LicenseEconomicsInput,
  LicenseEconomicsUpdate,
  LicenseModel,
  LicenseEconomicsInputStatus,
  LicenseEconomicsUpdateStatus,
  LicenseRateTier,
} from "@workspace/api-client-react";
import {
  LE_ACTOR,
  LICENSE_MODEL_LABELS,
  LICENSE_MODEL_ORDER,
  LE_STATUS_LABELS,
} from "./types";
import type { LicenseScope } from "./types";

// ─── Form payload union ────────────────────────────────────────────────────────

export type LicenseEconomicsFormPayload =
  | { mode: "create"; input: LicenseEconomicsInput }
  | { mode: "edit"; update: LicenseEconomicsUpdate };

// ─── Form state ────────────────────────────────────────────────────────────────

type FormState = {
  vendor: string;
  productWorkload: string;
  glAccount: string;
  currentModel: LicenseModel;
  futureModel: LicenseModel;
  seats: string;
  activeSeats: string;
  seatRate: string;
  usageUnit: string;
  currentMonthlyUsage: string;
  unitRate: string;
  fixedMonthlyComponent: string;
  minimumMonthlyCommitment: string;
  contractStartDate: string;
  contractEndDate: string;
  renewalDate: string;
  effectiveFiscalYear: string;
  effectivePeriod: string;
  sourceReference: string;
  approvalReference: string;
  status: LicenseEconomicsInputStatus;
  seatTiers: TierForm[];
  usageTiers: TierForm[];
  trueUpTiming: ContractualTrueUpTiming;
  trueUpFiscalYear: string;
  trueUpFiscalPeriod: string;
  trueUpAmount: string;
};

type TierForm = {
  label: string;
  threshold: string;
  rate: string;
  effectiveFiscalYear: string;
  effectivePeriod: string;
};

function tierToForm(tier: LicenseRateTier): TierForm {
  return {
    label: tier.label,
    threshold: String(tier.threshold),
    rate: String(tier.rate),
    effectiveFiscalYear: String(tier.effectiveFiscalYear),
    effectivePeriod: String(tier.effectivePeriod),
  };
}

function toDateInputValue(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : "";
}

function toFormState(
  record: LicenseEconomics | null,
  scope: LicenseScope
): FormState {
  if (!record) {
    return {
      vendor: "",
      productWorkload: "",
      glAccount: "",
      currentModel: "seat_based",
      futureModel: "usage_based",
      seats: "0",
      activeSeats: "0",
      seatRate: "0",
      usageUnit: "",
      currentMonthlyUsage: "0",
      unitRate: "0",
      fixedMonthlyComponent: "0",
      minimumMonthlyCommitment: "0",
      contractStartDate: "",
      contractEndDate: "",
      renewalDate: "",
      effectiveFiscalYear: "",
      effectivePeriod: "",
      sourceReference: "",
      approvalReference: "",
      status: "draft",
      seatTiers: [],
      usageTiers: [],
      trueUpTiming: "none",
      trueUpFiscalYear: "",
      trueUpFiscalPeriod: "",
      trueUpAmount: "0",
    };
  }
  return {
    vendor: record.vendor,
    productWorkload: record.productWorkload,
    glAccount: record.glAccount,
    currentModel: record.currentModel,
    futureModel: record.futureModel,
    seats: String(record.seats),
    activeSeats: String(record.activeSeats),
    seatRate: String(record.seatRate),
    usageUnit: record.usageUnit ?? "",
    currentMonthlyUsage: String(record.currentMonthlyUsage),
    unitRate: String(record.unitRate),
    fixedMonthlyComponent: String(record.fixedMonthlyComponent),
    minimumMonthlyCommitment: String(record.minimumMonthlyCommitment),
    contractStartDate: toDateInputValue(record.contractStartDate),
    contractEndDate: toDateInputValue(record.contractEndDate),
    renewalDate: toDateInputValue(record.renewalDate),
    effectiveFiscalYear:
      record.effectiveFiscalYear != null
        ? String(record.effectiveFiscalYear)
        : "",
    effectivePeriod:
      record.effectivePeriod != null ? String(record.effectivePeriod) : "",
    sourceReference: record.sourceReference,
    approvalReference: record.approvalReference ?? "",
    status: (record.status as LicenseEconomicsInputStatus) ?? "draft",
    seatTiers: (record.seatTiers ?? []).map(tierToForm),
    usageTiers: (record.usageTiers ?? []).map(tierToForm),
    trueUpTiming: record.trueUpTiming ?? "none",
    trueUpFiscalYear:
      record.trueUpFiscalYear != null ? String(record.trueUpFiscalYear) : "",
    trueUpFiscalPeriod:
      record.trueUpFiscalPeriod != null ? String(record.trueUpFiscalPeriod) : "",
    trueUpAmount: String(record.trueUpAmount ?? 0),
  };
}

function num(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function tierErrors(tiers: TierForm[]): string[] {
  const errors: string[] = [];
  tiers.forEach((tier, index) => {
    const prefix = `Tier ${index + 1}`;
    if (!tier.label.trim()) errors.push(`${prefix} needs a label.`);
    if (
      tier.threshold === "" ||
      num(tier.threshold) < 0 ||
      tier.rate === "" ||
      num(tier.rate) < 0
    ) {
      errors.push(`${prefix} needs a non-negative threshold and rate.`);
    }
    const fy = num(tier.effectiveFiscalYear);
    const period = num(tier.effectivePeriod);
    if (fy < 1900 || fy > 9999 || period < 1 || period > 12) {
      errors.push(`${prefix} needs a valid effective FY and period 1–12.`);
    }
    if (index > 0) {
      const previous = tiers[index - 1];
      const currentMonth = fy * 12 + period;
      const previousMonth =
        num(previous.effectiveFiscalYear) * 12 + num(previous.effectivePeriod);
      if (
        currentMonth < previousMonth ||
        (currentMonth === previousMonth &&
          num(tier.threshold) <= num(previous.threshold))
      ) {
        errors.push(
          `${prefix} must follow the prior effective date and have an ascending threshold within that schedule.`
        );
      }
    }
    const firstForSchedule = tiers.findIndex(
      (candidate) =>
        candidate.effectiveFiscalYear === tier.effectiveFiscalYear &&
        candidate.effectivePeriod === tier.effectivePeriod
    );
    if (firstForSchedule === index && num(tier.threshold) !== 0) {
      errors.push(`${prefix} starts a new effective schedule and must begin at threshold 0.`);
    }
  });
  return errors;
}

function toTiers(tiers: TierForm[]): LicenseRateTier[] {
  return tiers.map((tier) => ({
    label: tier.label.trim(),
    threshold: num(tier.threshold),
    rate: num(tier.rate),
    effectiveFiscalYear: num(tier.effectiveFiscalYear),
    effectivePeriod: num(tier.effectivePeriod),
  }));
}

// ─── Component ────────────────────────────────────────────────────────────────

export function LicenseEconomicsForm({
  record,
  scope,
  onSubmit,
  onCancel,
  isPending,
  error,
}: {
  record: LicenseEconomics | null;
  scope: LicenseScope;
  onSubmit: (payload: LicenseEconomicsFormPayload) => void;
  onCancel: () => void;
  isPending: boolean;
  error: string;
}) {
  const [form, setForm] = useState<FormState>(() => toFormState(record, scope));

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const usesUsage =
    form.currentModel !== "seat_based" || form.futureModel !== "seat_based";

  // Pre-validate: activeSeats must not exceed seats
  const seatsNum = num(form.seats);
  const activeSeatsNum = num(form.activeSeats);
  const seatsOverflow =
    form.seats !== "" &&
    form.activeSeats !== "" &&
    activeSeatsNum > seatsNum;
  const seatTierErrors = tierErrors(form.seatTiers);
  const usageTierErrors = tierErrors(form.usageTiers);
  const trueUpPeriodInvalid =
    num(form.trueUpFiscalPeriod) < 1 || num(form.trueUpFiscalPeriod) > 12;
  const trueUpError =
    (form.trueUpTiming === "annual" && trueUpPeriodInvalid) ||
    (form.trueUpTiming === "contractual" &&
      (!form.contractEndDate ||
        num(form.trueUpFiscalYear) < 1900 ||
        num(form.trueUpFiscalYear) > 9999 ||
        trueUpPeriodInvalid));

  const canSubmit = useMemo(
    () =>
      form.vendor.trim().length > 0 &&
      form.productWorkload.trim().length > 0 &&
      form.glAccount.trim().length > 0 &&
      form.sourceReference.trim().length > 0 &&
      !seatsOverflow &&
      seatTierErrors.length === 0 &&
      usageTierErrors.length === 0 &&
      !trueUpError,
    [
      form.vendor,
      form.productWorkload,
      form.glAccount,
      form.sourceReference,
      seatsOverflow,
      seatTierErrors.length,
      usageTierErrors.length,
      trueUpError,
    ]
  );

  const addTier = (key: "seatTiers" | "usageTiers") => {
    const prior = form[key].at(-1);
    set(key, [
      ...form[key],
      {
        label: "",
        threshold: prior ? String(num(prior.threshold) + 1) : "0",
        rate: "0",
        effectiveFiscalYear:
          prior?.effectiveFiscalYear || String(scope.fiscalYear),
        effectivePeriod: prior?.effectivePeriod || "1",
      },
    ]);
  };

  const updateTier = (
    key: "seatTiers" | "usageTiers",
    index: number,
    field: keyof TierForm,
    value: string
  ) =>
    set(
      key,
      form[key].map((tier, i) =>
        i === index ? { ...tier, [field]: value } : tier
      )
    );

  const moveTier = (
    key: "seatTiers" | "usageTiers",
    index: number,
    offset: number
  ) => {
    const next = [...form[key]];
    const destination = index + offset;
    if (destination < 0 || destination >= next.length) return;
    [next[index], next[destination]] = [next[destination], next[index]];
    set(key, next);
  };

  const removeTier = (key: "seatTiers" | "usageTiers", index: number) =>
    set(key, form[key].filter((_, i) => i !== index));

  // Effective-timing: period 12 is the last period of the FY; if empty we show
  // a hint that defaults to full-FY, and also explain that for contracts
  // starting at the next FY the user should enter FY+1 / period 1.
  const effFy = num(form.effectiveFiscalYear);
  const effPeriod = num(form.effectivePeriod);
  const nextFyPeriod1 =
    effPeriod === 12
      ? `FY${effFy + 1} / Period 1`
      : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    if (record) {
      // Edit: scope-free LicenseEconomicsUpdate
      const update: LicenseEconomicsUpdate = {
        vendor: form.vendor.trim(),
        productWorkload: form.productWorkload.trim(),
        currentModel: form.currentModel,
        futureModel: form.futureModel,
        seats: num(form.seats),
        activeSeats: num(form.activeSeats),
        seatRate: num(form.seatRate),
        usageUnit: form.usageUnit.trim() || null,
        currentMonthlyUsage: num(form.currentMonthlyUsage),
        unitRate: num(form.unitRate),
        fixedMonthlyComponent: num(form.fixedMonthlyComponent),
        minimumMonthlyCommitment: num(form.minimumMonthlyCommitment),
        seatTiers: toTiers(form.seatTiers),
        usageTiers: toTiers(form.usageTiers),
        trueUpTiming: form.trueUpTiming,
        trueUpFiscalYear:
          form.trueUpTiming === "contractual" ? num(form.trueUpFiscalYear) : null,
        trueUpFiscalPeriod:
          form.trueUpTiming === "none" ? null : num(form.trueUpFiscalPeriod),
        trueUpAmount:
          form.trueUpTiming === "none" ? 0 : num(form.trueUpAmount),
        contractStartDate: form.contractStartDate || null,
        contractEndDate: form.contractEndDate || null,
        renewalDate: form.renewalDate || null,
        effectiveFiscalYear: form.effectiveFiscalYear
          ? num(form.effectiveFiscalYear)
          : null,
        effectivePeriod: form.effectivePeriod
          ? num(form.effectivePeriod)
          : null,
        sourceReference: form.sourceReference.trim(),
        approvalReference: form.approvalReference.trim() || null,
        status: form.status as LicenseEconomicsUpdateStatus,
        actor: LE_ACTOR,
      };
      onSubmit({ mode: "edit", update });
    } else {
      // Create: full LicenseEconomicsInput with scope
      const input: LicenseEconomicsInput = {
        companyId: scope.companyId,
        fiscalYear: scope.fiscalYear,
        departmentCode: scope.departmentCode,
        glAccount: form.glAccount.trim(),
        vendor: form.vendor.trim(),
        productWorkload: form.productWorkload.trim(),
        currentModel: form.currentModel,
        futureModel: form.futureModel,
        seats: num(form.seats),
        activeSeats: num(form.activeSeats),
        seatRate: num(form.seatRate),
        usageUnit: form.usageUnit.trim() || null,
        currentMonthlyUsage: num(form.currentMonthlyUsage),
        unitRate: num(form.unitRate),
        fixedMonthlyComponent: num(form.fixedMonthlyComponent),
        minimumMonthlyCommitment: num(form.minimumMonthlyCommitment),
        seatTiers: toTiers(form.seatTiers),
        usageTiers: toTiers(form.usageTiers),
        trueUpTiming: form.trueUpTiming,
        trueUpFiscalYear:
          form.trueUpTiming === "contractual" ? num(form.trueUpFiscalYear) : null,
        trueUpFiscalPeriod:
          form.trueUpTiming === "none" ? null : num(form.trueUpFiscalPeriod),
        trueUpAmount:
          form.trueUpTiming === "none" ? 0 : num(form.trueUpAmount),
        contractStartDate: form.contractStartDate || null,
        contractEndDate: form.contractEndDate || null,
        renewalDate: form.renewalDate || null,
        effectiveFiscalYear: form.effectiveFiscalYear
          ? num(form.effectiveFiscalYear)
          : null,
        effectivePeriod: form.effectivePeriod
          ? num(form.effectivePeriod)
          : null,
        sourceReference: form.sourceReference.trim(),
        approvalReference: form.approvalReference.trim() || null,
        status: form.status,
        actor: LE_ACTOR,
      };
      onSubmit({ mode: "create", input });
    }
  };

  return (
    <form
      className="fi-drawer-body"
      onSubmit={handleSubmit}
      data-testid="license-economics-form"
    >
      <div className="sc-driver-form__desc" style={{ marginBottom: 14 }}>
        <p>
          License Economics are reusable scenario inputs. Saving or retiring a
          record updates the modeling library only — it does not change or stale
          the authoritative Forecast.
        </p>
      </div>

      {/* Identity */}
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">
            Vendor <span className="sc-required">*</span>
          </label>
          <input
            value={form.vendor}
            onChange={(e) => set("vendor", e.target.value)}
            placeholder="e.g. Atlassian"
            required
            data-testid="input-le-vendor"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">
            Product / workload <span className="sc-required">*</span>
          </label>
          <input
            value={form.productWorkload}
            onChange={(e) => set("productWorkload", e.target.value)}
            placeholder="e.g. Jira Cloud"
            required
            data-testid="input-le-product"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">
            GL account <span className="sc-required">*</span>
          </label>
          <input
            value={form.glAccount}
            onChange={(e) => set("glAccount", e.target.value)}
            placeholder="e.g. 6400-100"
            required
            disabled={!!record}
            title={record ? "GL account cannot be changed on edit" : undefined}
            data-testid="input-le-gl"
          />
          {record && (
            <span className="sc-field__hint">
              GL account is fixed after creation.
            </span>
          )}
        </div>
        <div className="sc-field">
          <label className="sc-label">Status</label>
          <select
            value={form.status}
            onChange={(e) =>
              set("status", e.target.value as LicenseEconomicsInputStatus)
            }
            data-testid="select-le-status"
          >
            {(
              Object.keys(LE_STATUS_LABELS) as LicenseEconomicsInputStatus[]
            ).map((s) => (
              <option key={s} value={s}>
                {LE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Models */}
      <div className="sc-label" style={{ margin: "16px 0 6px" }}>
        Commercial model
      </div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">Current model</label>
          <select
            value={form.currentModel}
            onChange={(e) =>
              set("currentModel", e.target.value as LicenseModel)
            }
            data-testid="select-le-current-model"
          >
            {LICENSE_MODEL_ORDER.map((m) => (
              <option key={m} value={m}>
                {LICENSE_MODEL_LABELS[m]}
              </option>
            ))}
          </select>
        </div>
        <div className="sc-field">
          <label className="sc-label">Future model</label>
          <select
            value={form.futureModel}
            onChange={(e) =>
              set("futureModel", e.target.value as LicenseModel)
            }
            data-testid="select-le-future-model"
          >
            {LICENSE_MODEL_ORDER.map((m) => (
              <option key={m} value={m}>
                {LICENSE_MODEL_LABELS[m]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Seat economics */}
      <div className="sc-label" style={{ margin: "16px 0 6px" }}>
        Seat economics
      </div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">Contracted seats</label>
          <input
            type="number"
            min={0}
            value={form.seats}
            onChange={(e) => set("seats", e.target.value)}
            data-testid="input-le-seats"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Active seats</label>
          <input
            type="number"
            min={0}
            value={form.activeSeats}
            onChange={(e) => set("activeSeats", e.target.value)}
            data-testid="input-le-active-seats"
          />
          {seatsOverflow && (
            <span
              className="sc-field__hint le-field-error"
              role="alert"
              data-testid="validation-active-seats"
            >
              Active seats ({activeSeatsNum}) cannot exceed contracted seats (
              {seatsNum}). Reduce active seats or increase contracted seats.
            </span>
          )}
        </div>
        <div className="sc-field">
          <label className="sc-label">Base seat rate ($ / seat / mo)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.seatRate}
            onChange={(e) => set("seatRate", e.target.value)}
            data-testid="input-le-seat-rate"
          />
          <span className="sc-field__hint">
            Fallback when no seat tier schedule is active. Active tiers supersede this rate.
          </span>
        </div>
      </div>
      <TierEditor
        title="Seat rate tiers"
        tiers={form.seatTiers}
        errors={seatTierErrors}
        rateUnit="$ / seat / mo"
        testId="seat"
        onAdd={() => addTier("seatTiers")}
        onChange={(index, field, value) =>
          updateTier("seatTiers", index, field, value)
        }
        onMove={(index, offset) => moveTier("seatTiers", index, offset)}
        onRemove={(index) => removeTier("seatTiers", index)}
      />

      {/* Usage economics */}
      <div className="sc-label" style={{ margin: "16px 0 6px" }}>
        Usage economics
      </div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">Usage unit</label>
          <input
            value={form.usageUnit}
            onChange={(e) => set("usageUnit", e.target.value)}
            placeholder="e.g. API calls, GB"
            data-testid="input-le-usage-unit"
          />
          {!usesUsage && (
            <span className="sc-field__hint">
              Both models are seat-based — usage inputs are informational.
            </span>
          )}
        </div>
        <div className="sc-field">
          <label className="sc-label">Current monthly usage</label>
          <input
            type="number"
            min={0}
            value={form.currentMonthlyUsage}
            onChange={(e) => set("currentMonthlyUsage", e.target.value)}
            data-testid="input-le-usage"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Base unit rate ($ / unit)</label>
          <input
            type="number"
            min={0}
            step="0.0001"
            value={form.unitRate}
            onChange={(e) => set("unitRate", e.target.value)}
            data-testid="input-le-unit-rate"
          />
          <span className="sc-field__hint">
            Fallback when no usage tier schedule is active. Active tiers supersede this rate.
          </span>
        </div>
      </div>
      <TierEditor
        title="Usage rate tiers"
        tiers={form.usageTiers}
        errors={usageTierErrors}
        rateUnit="$ / unit"
        testId="usage"
        onAdd={() => addTier("usageTiers")}
        onChange={(index, field, value) =>
          updateTier("usageTiers", index, field, value)
        }
        onMove={(index, offset) => moveTier("usageTiers", index, offset)}
        onRemove={(index) => removeTier("usageTiers", index)}
      />

      {/* Fixed + floor */}
      <div className="sc-label" style={{ margin: "16px 0 6px" }}>
        Fixed &amp; minimum commitment
      </div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">Fixed monthly component ($)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.fixedMonthlyComponent}
            onChange={(e) => set("fixedMonthlyComponent", e.target.value)}
            data-testid="input-le-fixed"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Minimum monthly commitment ($)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.minimumMonthlyCommitment}
            onChange={(e) => set("minimumMonthlyCommitment", e.target.value)}
            data-testid="input-le-minimum"
          />
        </div>
      </div>

      {/* Contractual true-up */}
      <div className="sc-label" style={{ margin: "16px 0 6px" }}>
        Contractual true-up
      </div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">True-up timing</label>
          <select
            value={form.trueUpTiming}
            onChange={(e) =>
              set("trueUpTiming", e.target.value as ContractualTrueUpTiming)
            }
            data-testid="select-le-true-up-timing"
          >
            <option value="none">No contractual true-up</option>
            <option value="annual">Annual true-up</option>
            <option value="contractual">At contract end / renewal</option>
          </select>
        </div>
        {form.trueUpTiming !== "none" && (
          <div className="sc-field">
            <label className="sc-label">True-up fiscal period (1–12)</label>
            <input
              type="number"
              min={1}
              max={12}
              value={form.trueUpFiscalPeriod}
              onChange={(e) => set("trueUpFiscalPeriod", e.target.value)}
              data-testid="input-le-true-up-period"
            />
            {trueUpError && (
              <span className="sc-field__hint le-field-error" role="alert">
                {form.trueUpTiming === "annual"
                  ? "Annual true-up requires a fiscal period from 1 to 12."
                  : "Contractual true-up requires a valid fiscal year and period mapping plus contract end evidence."}
              </span>
            )}
          </div>
        )}
        {form.trueUpTiming === "contractual" && (
          <div className="sc-field">
            <label className="sc-label">True-up fiscal year</label>
            <input
              type="number"
              min={1900}
              max={9999}
              value={form.trueUpFiscalYear}
              onChange={(e) => set("trueUpFiscalYear", e.target.value)}
              data-testid="input-le-true-up-fiscal-year"
            />
            <span className="sc-field__hint">
              Explicit accounting mapping; contract end date remains source evidence and does not set this period.
            </span>
          </div>
        )}
        {form.trueUpTiming !== "none" && (
          <div className="sc-field">
            <label className="sc-label">Contractual true-up amount ($)</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.trueUpAmount}
              onChange={(e) => set("trueUpAmount", e.target.value)}
              data-testid="input-le-true-up-amount"
            />
            <span className="sc-field__hint">
              Billed only at the configured timing and excluded from monthly run-rate.
            </span>
          </div>
        )}
      </div>

      {/* Timing */}
      <div className="sc-label" style={{ margin: "16px 0 6px" }}>
        Contract &amp; effective timing
      </div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">Contract start</label>
          <input
            type="date"
            value={form.contractStartDate}
            onChange={(e) => set("contractStartDate", e.target.value)}
            data-testid="input-le-start"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Contract end</label>
          <input
            type="date"
            value={form.contractEndDate}
            onChange={(e) => set("contractEndDate", e.target.value)}
            data-testid="input-le-end"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Renewal date</label>
          <input
            type="date"
            value={form.renewalDate}
            onChange={(e) => set("renewalDate", e.target.value)}
            data-testid="input-le-renewal"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Effective fiscal year</label>
          <input
            type="number"
            min={1900}
            max={9999}
            value={form.effectiveFiscalYear}
            onChange={(e) => set("effectiveFiscalYear", e.target.value)}
            data-testid="input-le-eff-fy"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Effective period (1–12)</label>
          <input
            type="number"
            min={1}
            max={12}
            value={form.effectivePeriod}
            onChange={(e) => set("effectivePeriod", e.target.value)}
            placeholder="Leave blank for full year"
            data-testid="input-le-eff-period"
          />
          {nextFyPeriod1 ? (
            <span className="sc-field__hint">
              Period 12 is the last period of FY{effFy}. For contracts effective
              at the start of the following year, use {nextFyPeriod1} instead.
            </span>
          ) : (
            <span className="sc-field__hint">
              Leave blank to apply across the full fiscal year. Enter 1–12 to
              restrict the effective start to a specific period.
            </span>
          )}
        </div>
      </div>

      {/* Governance */}
      <div className="sc-label" style={{ margin: "16px 0 6px" }}>
        Governance
      </div>
      <div className="sc-driver-form__fields">
        <div className="sc-field">
          <label className="sc-label">
            Source reference <span className="sc-required">*</span>
          </label>
          <input
            value={form.sourceReference}
            onChange={(e) => set("sourceReference", e.target.value)}
            placeholder="e.g. Order form #A-1042"
            required
            data-testid="input-le-source-ref"
          />
        </div>
        <div className="sc-field">
          <label className="sc-label">Approval reference</label>
          <input
            value={form.approvalReference}
            onChange={(e) => set("approvalReference", e.target.value)}
            placeholder="Optional"
            data-testid="input-le-approval-ref"
          />
        </div>
      </div>

      {error && (
        <div
          className="sc-form-error"
          role="alert"
          style={{ marginTop: 14 }}
          data-testid="license-economics-form-error"
        >
          <AlertCircle size={13} />
          <span>{error}</span>
        </div>
      )}

      <div className="sc-driver-form__actions" style={{ marginTop: 16 }}>
        <button
          type="button"
          className="tl-btn"
          onClick={onCancel}
          data-testid="button-le-cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="tl-btn primary"
          disabled={isPending || !canSubmit}
          data-testid="button-le-save"
        >
          {isPending ? (
            <>
              <Loader2 size={12} className="animate-spin" /> Saving…
            </>
          ) : record ? (
            "Save changes"
          ) : (
            "Create record"
          )}
        </button>
      </div>
    </form>
  );
}

function TierEditor({
  title,
  tiers,
  errors,
  rateUnit,
  testId,
  onAdd,
  onChange,
  onMove,
  onRemove,
}: {
  title: string;
  tiers: TierForm[];
  errors: string[];
  rateUnit: string;
  testId: string;
  onAdd: () => void;
  onChange: (index: number, field: keyof TierForm, value: string) => void;
  onMove: (index: number, offset: number) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="le-tier-editor" data-testid={`tier-editor-${testId}`}>
      <div className="le-tier-editor__head">
        <div>
          <strong>{title}</strong>
          <span>Ordered by effective FY/period, then ascending threshold.</span>
        </div>
        <button
          type="button"
          className="tl-btn"
          onClick={onAdd}
          data-testid={`button-add-${testId}-tier`}
        >
          <Plus size={12} /> Add tier
        </button>
      </div>
      {tiers.length === 0 && (
        <div className="le-tier-empty">No tiers — the scalar base rate applies.</div>
      )}
      {tiers.map((tier, index) => (
        <div className="le-tier-row" key={index}>
          <span className="le-tier-order">{index + 1}</span>
          <input
            aria-label={`Tier ${index + 1} label`}
            placeholder="Label"
            value={tier.label}
            onChange={(e) => onChange(index, "label", e.target.value)}
            data-testid={`input-${testId}-tier-label-${index}`}
          />
          <input
            aria-label={`Tier ${index + 1} threshold`}
            type="number"
            min={0}
            placeholder="Threshold"
            value={tier.threshold}
            onChange={(e) => onChange(index, "threshold", e.target.value)}
            data-testid={`input-${testId}-tier-threshold-${index}`}
          />
          <input
            aria-label={`Tier ${index + 1} rate`}
            type="number"
            min={0}
            step="0.0001"
            placeholder={rateUnit}
            value={tier.rate}
            onChange={(e) => onChange(index, "rate", e.target.value)}
            data-testid={`input-${testId}-tier-rate-${index}`}
          />
          <input
            aria-label={`Tier ${index + 1} effective fiscal year`}
            type="number"
            min={1900}
            max={9999}
            placeholder="FY"
            value={tier.effectiveFiscalYear}
            onChange={(e) => onChange(index, "effectiveFiscalYear", e.target.value)}
            data-testid={`input-${testId}-tier-fy-${index}`}
          />
          <input
            aria-label={`Tier ${index + 1} effective period`}
            type="number"
            min={1}
            max={12}
            placeholder="P"
            value={tier.effectivePeriod}
            onChange={(e) => onChange(index, "effectivePeriod", e.target.value)}
            data-testid={`input-${testId}-tier-period-${index}`}
          />
          <div className="le-tier-actions">
            <button
              type="button"
              className="sc-icon-btn"
              onClick={() => onMove(index, -1)}
              disabled={index === 0}
              aria-label={`Move tier ${index + 1} up`}
              data-testid={`button-${testId}-tier-up-${index}`}
            >
              <ArrowUp size={12} />
            </button>
            <button
              type="button"
              className="sc-icon-btn"
              onClick={() => onMove(index, 1)}
              disabled={index === tiers.length - 1}
              aria-label={`Move tier ${index + 1} down`}
              data-testid={`button-${testId}-tier-down-${index}`}
            >
              <ArrowDown size={12} />
            </button>
            <button
              type="button"
              className="sc-icon-btn"
              onClick={() => onRemove(index)}
              aria-label={`Remove tier ${index + 1}`}
              data-testid={`button-remove-${testId}-tier-${index}`}
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      ))}
      {errors.length > 0 && (
        <div className="le-tier-errors" role="alert" data-testid={`validation-${testId}-tiers`}>
          {errors.map((message) => (
            <span key={message}>{message}</span>
          ))}
        </div>
      )}
    </div>
  );
}
